import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { ClassDetailResponse } from "../../../../interfaces/classDetail";
import { useHttpClient } from "../../../../hooks/useHttpClient";
import { useLocalization } from "../../../../stores/localizationContext";
import { useUserContext } from "../../../../stores/userContext";
import SeatsSummary from "./SeatsSummary/SeatsSummary";
import StudentsList from "./StudentsList/StudentsList";
import { AlertPopup } from "../../../Popups/AlertPopup";
import "./ClassDetail.scss";

const ClassDetail: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useUserContext();
  const api = useHttpClient();
  const { t } = useLocalization();

  const [data, setData] = useState<ClassDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchClassDetail = useCallback(async () => {
    if (!user?.userId || !classId) return;

    setError(null);

    try {
      const endpoint = import.meta.env.VITE_GET_CLASS_DETAIL
        .replace("{educator_id}", user.userId)
        .replace("{class_id}", classId);

      const response = await api.get(endpoint);
      setData(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError(t("unauthorized_access_error"));
      } else if (status === 404) {
        setError(t("class_not_found", "Class not found or not available."));
      } else if (status === 400) {
        setError(t("invalid_request", "Invalid request. Please check the class ID."));
      } else {
        setError(t("generic_error", "Something went wrong. Please try again later."));
      }
    }
  }, [user?.userId, classId]);

  useEffect(() => {
    fetchClassDetail();
  }, [fetchClassDetail]);

  if (error) {
    return (
      <main className="class-detail">
        <div className="container">
          <div className="class-detail__error">{error}</div>
        </div>
      </main>
    );
  }

  if (!data) return null;

  const subtitleParts = [
    t(data.grade_localization_key, data.grade_localization_key),
    data.class_semester_localization_key
      ? t(data.class_semester_localization_key, data.class_semester_localization_key)
      : null,
    t(data.class_type_localization_key, data.class_type),
  ].filter(Boolean);

  return (
    <main className="class-detail">
      <div className="container">
        <div className="class-detail__header">
          <h1 className="class-detail__title" dir="auto">{data.class_title}</h1>
          <p className="class-detail__subtitle" dir="auto">{subtitleParts.join(" \u2022 ")}</p>
        </div>

        <SeatsSummary data={data} />
        <StudentsList
          data={data}
          classId={classId!}
          onRefresh={() => {
            setSuccessMessage(t("student_added_success", "Student has been successfully added to the class."));
            fetchClassDetail();
          }}
        />
      </div>

      {successMessage && (
        <AlertPopup
          title={t("success", "Success")}
          message={successMessage}
          confirmText={t("ok_label")}
          onConfirm={() => setSuccessMessage(null)}
        />
      )}
    </main>
  );
};

export default ClassDetail;
